<template><div class="main">
  <sticky-bar @back="$router.push('/billing')" v-if="accounts && (accounts.length > 1)">
    Billing Account
    <small v-html="accountSummary.name" v-if="accountSummary" />
    <small v-else-if="accountsLoading">Loading...</small>
    <small v-else class="error">Error</small>
  </sticky-bar>
  <sticky-bar v-else>
    Billing Account
    <small v-html="accountSummary.name" v-if="accountSummary" />
    <small v-else-if="accountsLoading">Loading...</small>
    <small v-else class="error">Error</small>
  </sticky-bar>
  <div class="padded">
    <button
        v-if="(!accountSummary && !accountsLoading) || (!account && !ref)"
        @click="initialize"
      >An error occurred. Click here to try again.</button>
    <loading v-else-if="!accountSummary || !account" />
    <div v-else>
      <table class="account-summary selectable">
        <tr>
          <th>Account #</th>
          <td>{{ accountSummary.id }}</td>
        </tr>
        <tr>
          <th>Name</th>
          <td>{{ accountSummary.name }}</td>
        </tr>
        <tr>
          <th>Owner</th>
          <td>
            {{accountSummary.owner.firstName}}
            {{accountSummary.owner.lastName}}
            ({{accountSummary.owner.email}})
          </td>
        </tr>
        <tr v-if="account.invoice">
          <th>Last Invoice</th>
          <td>{{account.invoice.created}} (USD${{account.invoice.amount}})</td>
        </tr>
        <tr>
          <th>Status</th>
          <td v-if="!account.invoice">No invoice has been issued yet.</td>
          <td v-else-if="account.invoice.paid">Paid in full. Thank you!</td>
          <td v-else>
            USD${{account.invoice.amount}} due.
            <template v-if="account.invoice.nextAttempt">
              Payment will be attempted
              <template v-if="account.last4">
                on card ending with {{account.last4}}
              </template>
              on {{account.invoice.nextAttempt}}.
            </template>
            <template v-else>
              Payment will not be attempted automatically until the next invoice is issued.
            </template>
          </td>
        </tr>
        <tr v-if="account.last4 && account.invoice && !account.invoice.paid && account.invoice.attempts">
          <th>&nbsp;</th>
          <td class="error">
            <span class="material-icons">warning</span>
            <template v-if="account.invoice.attempts === 1">
              There has been 1 unsuccessful attempt
            </template>
            <template v-else>
              There have been {{ account.invoice.attempts }} unsuccessful attempts
            </template>
            to charge for the amount due on the latest invoice. To avoid deactivation of your
            account, make sure to add a valid card using the "Update&nbsp;Credit&nbsp;Card" button
            below.
          </td>
        </tr>
        <tr v-if="account.last4">
          <th class="card-on-file">Card on File</th>
          <td>*{{account.last4}}
            <button @click="checkout('Update Payment Method')" :disabled="!!ref">Update Credit Card</button>
            <loading v-if="ref" />
          </td>
        </tr>
        <tr v-else-if="account.invoice && account.invoice.amount > 0">
          <th>&nbsp;</th>
          <td class="error">
            <span class="material-icons">warning</span>
            There is no credit card on file! To avoid deactivation of your account, make sure to
            add a card using the "Add&nbsp;Credit&nbsp;Card" button below.
          </td>
        </tr>
        <tr v-if="!account.last4">
          <th>&nbsp;</th>
          <td>
            <button @click="checkout('Add Payment Method')" :disabled="!!ref">Add Credit Card</button>
            <loading v-if="ref" />
          </td>
        </tr>
      </table>
      <div class="menu">
        <button :class="{active:page==='locomotives'}" @click="page='locomotives'">Locomotives</button>
        <button :class="{active:page==='invoices'}" @click="page='invoices'">Invoices</button>
      </div>
      <div v-if="page==='locomotives'">
        <span v-if="account.locomotives.length === 0">There are no locomotives on this account.</span>
        <table v-else class="locomotives selectable">
        <thead><tr>
          <th>Name</th>
          <th>Plan</th>
          <th>Term End</th>
          <th>Auto-Renew</th></tr></thead>
        <tbody>
        <tr v-for="loco in account.locomotives" :key="loco.id">
          <td v-html="loco.name" />
          <td v-html="loco.plan" />
          <td v-html="loco.termEnd" />
          <td v-html="loco.autoRenew" />
        </tr>
        </tbody>
        </table>
      </div>
      <div v-else-if="page==='invoices'">
        <span v-if="account.invoices.length === 0">No invoices have been issued yet.</span>
        <table v-else class="invoices selectable">
        <thead><tr>
          <th>No</th>
          <th>Date</th>
          <th>Amount</th>
          <th>Details</th></tr></thead>
        <tbody>
        <tr v-for="invoice in account.invoices" :key="invoice.id">
          <td v-html="invoice.id" />
          <td v-html="invoice.created" />
          <td v-html="invoice.amount" />
          <td>
            <a :href="`/invoice/${invoice.id}/`" target="_blank">Details</a>
          </td>
        </tr>
        </tbody>
        </table>
      </div>
    </div>
  </div>
</div></template>
<script>
import Loading from './Loading'
import StickyBar from './StickyBar'
import logoStripe from '../assets/logo-stripe.png'
export default {
  name: 'billing-account',
  components: { Loading, StickyBar },
  props: ['aid'],
  created() {
    this.initialize()
    const script = document.createElement('script')
    script.setAttribute('src', 'https://checkout.stripe.com/checkout.js')
    document.head.appendChild(script)
  },
  destroyed() {
    if (this.ref) {
      this.ref.cancel()
      this.ref = null
    }
  },
  data() {
    return {
      ref: null,
      account: null,
      stripeError: null,
      page: 'locomotives',
    }
  },
  computed: {
    accounts() { return this.$store.state.billingAccounts },
    accountsLoading() { return (this.$store.state.billingAccountsRef !== null) },
    accountSummary() {
      const aid = parseInt(this.aid, 10)
      return this.accounts && this.accounts.filter(a => (a.id === aid))[0]
    },
  },
  methods: {
    async initialize() {
      this.$store.dispatch('fetchBillingAccounts')
      if (this.ref || this.account) return
      let ref = this.ref = { aid: parseInt(this.aid, 10) }
      try {
        this.account = await api.billingAccount(ref)
      } finally {
        this.ref = null
      }
    },
    checkout(description) {
      this.stripeHandler = StripeCheckout.configure({
        key: api.stripePK,
        locale: 'auto',
        token: (token) => { this.updateCardToken(token) },
        image: logoStripe,
        description: description,
        currency: 'usd',
        name: 'TMV Control Systems, Inc.',
        panelLabel: 'Update',
      })
      this.stripeHandler.open({
        email: this.accountSummary.owner.email,
      })
    },
    async updateCardToken(token) {
      this.stripeError = false
      this.ref = { aid: parseInt(this.aid, 10), stripeToken: token.id }
      try {
        this.account = await api.billingAccount(this.ref)
      } catch(e) {
        console.error(e)
        this.stripeError = true
      } finally {
        this.ref = null
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.account-summary {
  th {
    white-space: nowrap;
    text-align: left;
    vertical-align: top;
    padding: 5px 0;
    &.card-on-file {
      vertical-align: middle;
    }
  }
  td {
    padding: 5px 0 5px 15px;
  }
  .error {
    .material-icons {
      vertical-align: bottom;
    }
  }
}
.menu {
  display: inline-flex;
  border-radius: 5px;
  flex-wrap: wrap;
  padding: 5px;
  margin: 10px 0 5px 0;
  gap: 5px;
  background-color: #aaa;
  button.active {
    background-color: #fff;
  }
}
.locomotives, .invoices {
  border-collapse: collapse;
  th, td { border: 1px solid #000; padding: 5px 10px; }
}
</style>
